import React from 'react';
import { FeatureRow, SectionLabel } from './FeatureRow';
import { useWelcomeOffer } from '../../context/WelcomeOfferContext';
import { applyCarePlanMode } from './priceMath';
import { CARE_PRICES } from '../../constants/pricing';
import { MONO } from '../../constants/fonts';

const APP_ESSENTIAL_CARE_PRICE = CARE_PRICES.app.essential;
const APP_ACTIVE_CARE_PRICE = CARE_PRICES.app.active;

function renderCarePrice(original, mode, percent, custom) {
  if (mode === 'none') return `£${original}`;
  return (
    <>
      <span style={s.strikePrice}>£{original}</span>
      {mode === 'free' ? 'Free*' : `£${applyCarePlanMode(original, mode, percent, custom)}`}
    </>
  );
}

const ADDONS_FEATURED = [
  'Booking & scheduling',
  'Loyalty & rewards',
  'Push notifications',
  'User accounts',
  'Analytics & user tracking',
  'In-app chat',
  'Remote content management',
];

const ADDONS_MORE = [
  'Map & location',
  'Social sharing',
  'Instagram integration',
  'Facebook integration',
  'Photo gallery',
  'Digital menu / price list',
  'Event listings',
  'Class timetable',
  'Staff profiles',
  'Reviews & ratings',
  'QR code integration',
  'Referral programme',
  'Multi-language support',
  'Gift cards',
];

function AppPackages({ addOnsExpanded, setAddOnsExpanded }) {
  const { offer } = useWelcomeOffer();
  const isAppCategory = offer?.category === 'app';
  const essentialMode = isAppCategory ? (offer?.essentialPlanMode || 'none') : 'none';
  const essentialPercent = offer?.essentialPlanPercent;
  const essentialCustom = offer?.essentialPlanCustom;
  const activeMode = isAppCategory ? (offer?.activePlanMode || 'none') : 'none';
  const activePercent = offer?.activePlanPercent;
  const activeCustom = offer?.activePlanCustom;

  return (
    <div>
      <SectionLabel>App packages</SectionLabel>
      <h2 style={s.sectionHeadline}>From first idea to the App Store.</h2>
      <p style={{ ...s.sectionSub, marginBottom: 40 }}>
        Cross-platform apps for iOS and Android. A care plan is mandatory on every build. Prices are a guide and every app is scoped individually before we quote.
      </p>

      <div className="cards-grid" style={s.appCardsGrid}>

        {/* Basic */}
        <div className="pricing-card" style={s.appCard}>
          <div style={s.appCardTop}>
            <div style={{ ...s.appTier, background: 'rgba(52,211,153,0.12)', color: '#34d399' }}>
              Basic
            </div>
            <div>
              <p style={s.appCardPrice}>from £3,000</p>
              <p style={s.appCardSub}>The Starter MVP</p>
            </div>
          </div>
          <p style={s.cardDesc}>
            For local service businesses needing a simple, polished mobile presence.
          </p>
          <div style={s.featureList}>
            <FeatureRow label="4–5 static screens (Home, About, Services, Contact)" included />
            <FeatureRow label="Tap-to-call, opening hours, social links" included />
            <FeatureRow label="Contact form, enquiries direct to your inbox" included />
            <FeatureRow label="User accounts / logins" />
            <FeatureRow label="Push notifications" />
            <FeatureRow label="Payment handling" />
          </div>
        </div>

        {/* Medium */}
        <div className="pricing-card" style={{ ...s.appCard, borderColor: 'rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.05)' }}>
          <div style={s.appCardTop}>
            <div style={{ ...s.appTier, background: 'rgba(167,139,250,0.15)', color: '#a78bfa' }}>
              Medium
            </div>
            <div>
              <p style={{ ...s.appCardPrice, color: '#a78bfa' }}>from £7,500</p>
              <p style={s.appCardSub}>The Dynamic App</p>
            </div>
          </div>
          <p style={s.cardDesc}>
            For businesses needing bookings, user management, or community features. Custom frontend and a dedicated backend.
          </p>
          <div style={s.featureList}>
            <FeatureRow label="Everything in Basic" included />
            <FeatureRow label="Up to 10 dynamic screens" included />
            <FeatureRow label="User authentication (registration, login, profiles)" included />
            <FeatureRow label="Push notifications the owner can send" included />
            <FeatureRow label="Booking / scheduling (Calendly or database-driven)" included />
            <FeatureRow label="CMS admin dashboard so you can update content without us" included />
          </div>
        </div>

      </div>

      {/* Add-ons — full width below both cards */}
      <p style={s.addOnsTagline}>If you're thinking it, we can probably do it. Here are a few ideas to get you started:</p>
      <button onClick={() => setAddOnsExpanded(e => !e)} style={s.addOnsToggle}>
        <span style={s.addOnsToggleLeft}>
          <span style={s.addOnsToggleDot} />
          <span>
            <span style={s.addOnsToggleTitle}>Need inspiration?</span>
            <span style={s.addOnsToggleSub}>Browse add-ons available across both plans</span>
          </span>
        </span>
        <span style={{
          ...s.addOnsToggleChevron,
          transform: addOnsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.5"
               strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div style={{
        ...s.addOnsExpandedBox,
        maxHeight: addOnsExpanded ? 600 : 0,
        opacity: addOnsExpanded ? 1 : 0,
        marginTop: addOnsExpanded ? 12 : 0,
        paddingTop: addOnsExpanded ? 16 : 0,
        paddingBottom: addOnsExpanded ? 16 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.35s ease, opacity 0.25s ease, margin-top 0.35s ease, padding 0.35s ease',
      }}>
        {[...ADDONS_FEATURED, ...ADDONS_MORE].map(a => (
          <span key={a} style={s.addOnPill}>{a}</span>
        ))}
      </div>

      {/* App care plans */}
      <p id="app-care-plans" style={{ ...s.cardLabel, margin: '48px 0 8px', color: '#9ca3af' }}>App care plans</p>
      <p style={{ fontSize: '0.82rem', color: '#4a4f66', margin: '0 0 20px', lineHeight: 1.6 }}>
        Mandatory on every build. Apple Developer (£79/yr) and Google Play (£20 one-off) accounts are yours, billed to you directly.
      </p>
      <div className="care-grid" style={s.careGrid}>

        <div className="pricing-card" style={s.careCard}>
          <div style={{ marginBottom: 16 }}>
            <p style={s.carePlanName}>App Essential</p>
            <p style={{ fontSize: '0.7rem', color: '#4a4f66', fontFamily: MONO, letterSpacing: '0.06em', margin: '2px 0 8px' }}>
              For Basic builds
            </p>
            <p style={{ ...s.carePrice, color: '#d8dae8' }}>
              {renderCarePrice(APP_ESSENTIAL_CARE_PRICE, essentialMode, essentialPercent, essentialCustom)}
              {essentialMode !== 'free' && <span style={s.perMonth}>/mo</span>}
            </p>
          </div>
          <div style={s.featureList}>
            <FeatureRow label="iOS & Android OS compatibility updates" included />
            <FeatureRow label="App Store / Play Store compliance resubmissions" included />
            <FeatureRow label="Crash monitoring & bug fixes" included />
            <FeatureRow label="1 hr/mo content changes" />
            <FeatureRow label="Analytics dashboard" />
            <FeatureRow label="Monthly report" />
          </div>
        </div>

        <div className="pricing-card" style={{ ...s.careCard, borderColor: 'rgba(167,139,250,0.35)', background: 'rgba(167,139,250,0.05)' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={s.carePlanName}>App Active</p>
              <span style={{ ...s.badge, background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontSize: '0.62rem' }}>
                Recommended
              </span>
            </div>
            <p style={{ fontSize: '0.7rem', color: '#4a4f66', fontFamily: MONO, letterSpacing: '0.06em', margin: '2px 0 8px' }}>
              For Medium builds
            </p>
            <p style={{ ...s.carePrice, color: '#a78bfa' }}>
              {renderCarePrice(APP_ACTIVE_CARE_PRICE, activeMode, activePercent, activeCustom)}
              {activeMode !== 'free' && <span style={s.perMonth}>/mo</span>}
            </p>
          </div>
          <div style={s.featureList}>
            <FeatureRow label="Everything in App Essential" included />
            <FeatureRow label="1 hour/mo content or config changes" included />
            <FeatureRow label="Analytics dashboard" included />
            <FeatureRow label="Monthly performance report" included />
            <FeatureRow label="Priority response" included />
          </div>
        </div>

      </div>
    </div>
  );
}

const s = {
  sectionHeadline: {
    fontSize: 'clamp(1.6rem, 5vw, 2.6rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    lineHeight: 1.15,
    color: '#f0f0f5',
    margin: '0 0 16px',
  },
  sectionSub: {
    fontSize: '0.92rem',
    color: '#6b7080',
    lineHeight: 1.75,
    maxWidth: 560,
    margin: '0 0 40px',
  },
  appCardsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 0,
  },
  appCard: {
    background: '#16161c',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: '28px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  appCardTop: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  appTier: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    fontFamily: MONO,
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: '5px 12px',
    borderRadius: 20,
  },
  appCardPrice: {
    fontSize: 'clamp(1.4rem, 3.5vw, 1.9rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: '#34d399',
    margin: 0,
    lineHeight: 1,
  },
  appCardSub: {
    fontSize: '0.78rem',
    color: '#4a4f66',
    fontFamily: MONO,
    letterSpacing: '0.04em',
    marginTop: 6,
  },
  cardDesc: {
    fontSize: '0.84rem',
    color: '#6b7080',
    lineHeight: 1.7,
    margin: '0 0 20px',
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  addOnsTagline: {
    fontSize: '0.96rem',
    color: '#6b7080',
    fontStyle: 'italic',
    margin: '20px 0 0',
  },
  addOnsToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 16,
    padding: '14px 18px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s, background 0.15s',
  },
  addOnsToggleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  addOnsToggleDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#34d399',
    boxShadow: '0 0 8px rgba(52,211,153,0.5)',
    flexShrink: 0,
  },
  addOnsToggleTitle: {
    display: 'block',
    fontSize: '0.88rem',
    fontWeight: 600,
    color: '#d8dae8',
  },
  addOnsToggleSub: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#4a4f66',
    marginTop: 2,
  },
  addOnsToggleChevron: {
    color: '#4a4f66',
    transition: 'transform 0.2s ease',
    flexShrink: 0,
  },
  addOnsExpandedBox: {
    padding: '0 18px',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 12,
  },
  addOnPill: {
    display: 'inline-block',
    fontSize: '0.72rem',
    color: '#6b7080',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: '3px 10px',
    marginRight: 6,
    marginBottom: 6,
  },
  cardLabel: {
    fontFamily: MONO,
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#4a4f66',
    margin: '0 0 8px',
  },
  careGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  careCard: {
    background: '#16161c',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: '24px',
  },
  carePlanName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#d8dae8',
    margin: '0 0 4px',
  },
  carePrice: {
    fontSize: '2rem',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    margin: 0,
    lineHeight: 1,
  },
  strikePrice: {
    fontSize: '0.5em',
    fontWeight: 600,
    color: '#4a4f66',
    textDecoration: 'line-through',
    marginRight: 6,
  },
  perMonth: {
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#4a4f66',
    marginLeft: 2,
  },
  badge: {
    fontFamily: MONO,
    fontSize: '0.64rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '4px 10px',
    borderRadius: 20,
    flexShrink: 0,
  },
};

export default AppPackages;
